import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_URI } from "../../../constants/GlobalConstants";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  Grid,
  Pagination,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Chip,
  IconButton,
  Fade,
  Container,
  Paper,
  Skeleton,
  TextField,
  InputAdornment,
  CardActionArea,
  Modal,
  Backdrop,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Link } from "react-router-dom";

const ImageToOrderListComp = () => {
  const userLogin = useSelector((state) => state.userSignIn);
  const { userInfo } = userLogin;
  const [imageData, setImageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const fetchImageData = async (pageNumber) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URI}/api/img-to-text/list`, {
        params: { page: pageNumber, limit: 12 },
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      const { data, totalPages } = response.data;
      setImageData(data);
      setFilteredData(data);
      setTotalPages(totalPages);
    } catch (error) {
      console.error("Error fetching image data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImageData(page);
  }, [page, userInfo.token]);

  useEffect(() => {
    const filtered = imageData.filter((item) =>
      item.status.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
  }, [searchTerm, imageData]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URI}/api/img-to-text/${deleteId}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      setImageData((prevData) =>
        prevData.filter((item) => item._id !== deleteId)
      );
      setIsModalOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setDeleteId(null);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowImageModal(true);
  };

  const LoadingSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(12)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${index}`}>
          <Card>
            <Skeleton variant="rectangular" height={200} />
            <CardContent>
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="text" width="80%" />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="xl">
      <Box>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: "transparent",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              mb: 4,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
            }}
          >
            <Box flex={1}>
              <Typography
                variant="h4"
                fontWeight="bold"
                color="primary"
                sx={{ mb: 1 }}
              >
                AI Image Processing Results
              </Typography>
              <Typography variant="body2" color="white">
                Manage and view your processed images
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <TextField
                size="small"
                placeholder="Search by status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "white" }} />{" "}
                      {/* Icon color to white */}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "white" },
                    "&:hover fieldset": { borderColor: "white" },
                    "&.Mui-focused fieldset": { borderColor: "white" },
                  },
                  input: { color: "white" },
                  "& .MuiInputAdornment-root": { color: "white" },
                }}
              />
            </Box>
          </Box>

          {loading ? (
            <LoadingSkeleton />
          ) : (
            <Fade in={!loading}>
              <Grid container spacing={3}>
                {filteredData.map((item, index) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item._id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      <CardActionArea onClick={() => handleImageClick(item)}>
                        <CardMedia
                          component="img"
                          height={200}
                          image={item.signedUrl}
                          alt={`Processed Image ${index + 1}`}
                          sx={{
                            objectFit: "cover",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                          }}
                        />
                      </CardActionArea>
                      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                         <Button
  size="small"
  variant="outlined"
  color="primary"
  component={Link}
  to={`/order/img-to-order/${item._id}`}
  startIcon={<VisibilityIcon />}
  sx={{
    textTransform: "none",
    "&:hover": {
      backgroundColor: "primary.lighter",
      transform: "scale(1.05)",
      transition: "transform 0.2s",
    },
  }}
>
  View Order
</Button>
                          <Chip
                            label={item.status}
                            color={
                              item.status === "completed"
                                ? "success"
                                : "warning"
                            }
                            size="small"
                            sx={{
                              borderRadius: 1,
                              textTransform: "capitalize",
                            }}
                          />

                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(item._id);
                            }}
                            sx={{
                              "&:hover": {
                                backgroundColor: "error.lighter",
                                transform: "rotate(90deg)",
                                transition: "transform 0.2s",
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 2,
                            color: "text.secondary",
                          }}
                        >
                          <CalendarTodayIcon sx={{ fontSize: 16, mr: 1 }} />
                          <Typography variant="caption">
                            {new Date(item.createdAt).toLocaleDateString()}
                          </Typography>
                        </Box>

                        {item.data.slice(0, 3).map((dim, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: 1,
                              p: 1.5,
                              backgroundColor: "action.hover",
                              borderRadius: 1,
                              "&:hover": {
                                backgroundColor: "action.selected",
                              },
                            }}
                          >
                            <AspectRatioIcon
                              sx={{
                                fontSize: 16,
                                mr: 1,
                                color: "primary.main",
                              }}
                            />
                            <Typography variant="caption">
                              {dim.width} × {dim.height} px
                            </Typography>
                          </Box>
                        ))}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Fade>
          )}

          {!loading && filteredData.length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                backgroundColor: "action.hover",
                borderRadius: 2,
                mt: 4,
              }}
            >
              <ImageIcon
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No images found
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {searchTerm
                  ? "Try adjusting your search terms"
                  : "Upload some images to get started"}
              </Typography>
              {!searchTerm && (
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 2 }}
                  startIcon={<ImageIcon />}
                >
                  Upload Images
                </Button>
              )}
            </Box>
          )}

          {!loading && totalPages > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 6,
                pb: 2,
                color: "white",
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "white",
                    borderColor: "white",
                  },
                  "& .MuiPaginationItem-icon": {
                    color: "white",
                  },
                }}
              />
            </Box>
          )}

          <Dialog open={isModalOpen} onClose={closeModal}>
            <DialogTitle>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                Confirm Deletion
                <IconButton size="small" onClick={closeModal}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete this image? This action cannot
                be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeModal}>Cancel</Button>
              <Button
                onClick={handleDelete}
                variant="contained"
                color="error"
                startIcon={<DeleteOutlineIcon />}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          <Modal
            open={showImageModal}
            onClose={() => setShowImageModal(false)}
            BackdropComponent={Backdrop}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                maxWidth: "90vw",
                maxHeight: "90vh",
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 2,
                borderRadius: 2,
                outline: "none",
              }}
            >
              {selectedImage && (
                <Box sx={{ position: "relative" }}>
                  <IconButton
                    onClick={() => setShowImageModal(false)}
                    sx={{
                      position: "absolute",
                      right: -12,
                      top: -12,
                      backgroundColor: "background.paper",
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  <img
                    src={selectedImage.signedUrl}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "80vh",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Modal>
        </Paper>
      </Box>
    </Container>
  );
};

export default ImageToOrderListComp;
